import { useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCw } from "lucide-react";
import api from "@/lib/api";

const DELIVERED = "delivered";
const FAILED = ["failed", "undelivered", "not_configured", "invalid_phone"];

export const STATUS_FILTERS = [
  { v: "all", l: "Alle" }, { v: "delivered", l: "Zugestellt" }, { v: "pending", l: "Unterwegs" }, { v: "failed", l: "Fehlgeschlagen" },
];
export const KIND_FILTERS = [{ v: "all", l: "Alle Arten" }, { v: "otp", l: "Login-Code" }, { v: "customer", l: "Kunden-Nachricht" }];

export const PURPOSE_DE = {
  otp_login: "Login-Code", magic_link: "Login-Code", otp_register: "Login-Code",
  customer_signed: "Auftrag unterschrieben", twilio_callback_orphan: "Unbekannt",
};

function statusOf(s) {
  const v = (s || "").toLowerCase();
  if (v === DELIVERED) return { l: "Zugestellt", c: "bg-[#00E575]/15 text-[#00E575] border-[#00E575]/40" };
  if (FAILED.includes(v)) return { l: "Fehlgeschlagen", c: "bg-rose-500/15 text-rose-300 border-rose-500/40" };
  return { l: "Unterwegs", c: "bg-sky-500/15 text-sky-300 border-sky-500/40" };
}

function ResendBtn({ row, onDone }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/sms/${row.id}/resend`);
      if (data.ok) toast.success("SMS erneut gesendet");
      else toast.error(`Erneut fehlgeschlagen: ${data.reason_de || data.status}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erneut senden fehlgeschlagen");
    } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={go} disabled={busy} data-testid={`sms-resend-${row.id}`}
            className="h-8 px-2.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[12px] font-bold text-zinc-100 inline-flex items-center gap-1.5 disabled:opacity-50">
      {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />} Erneut senden
    </button>
  );
}

function Row({ row, onDone }) {
  const st = statusOf(row.status);
  const otp = PURPOSE_DE[row.purpose] === "Login-Code";
  return (
    <tr className="border-b border-zinc-800/50 last:border-0 align-top" data-testid={`sms-log-row-${row.id}`}>
      <td className="py-2 pr-3 text-zinc-400 whitespace-nowrap">{row.created_at ? new Date(row.created_at).toLocaleString("de-DE") : "—"}</td>
      <td className="py-2 pr-3 font-mono text-zinc-200 whitespace-nowrap">{row.phone_masked || "—"}</td>
      <td className="py-2 pr-3 text-zinc-300 whitespace-nowrap">{PURPOSE_DE[row.purpose] || row.purpose || "—"}</td>
      <td className="py-2 pr-3"><span className={`text-[11.5px] font-bold px-2 py-0.5 rounded-md border whitespace-nowrap ${st.c}`} data-testid={`sms-status-${row.id}`}>{st.l}</span></td>
      <td className="py-2 pr-3 text-rose-200 min-w-[180px]" data-testid={`sms-reason-${row.id}`}>
        {row.reason_de || ""}
        {row.reason_de && otp && <div className="text-[11px] text-zinc-500">Nutzer kann einfach einen neuen Code anfordern.</div>}
        {row.resent_log_id && <div className="text-[11px] text-zinc-500">Erneut gesendet</div>}
      </td>
      <td className="py-2 text-right">{row.can_resend && <ResendBtn row={row} onDone={onDone} />}</td>
    </tr>
  );
}

export function SmsLogTable({ rows, onReload }) {
  return (
    <div className="overflow-x-auto -mx-5 px-5">
      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="text-[11.5px] uppercase tracking-widest text-zinc-300 border-b border-zinc-800">
            {["Zeit", "Telefon", "Art", "Status", "Grund", ""].map((h, i) => <th key={i} className="text-left py-2 pr-3 font-bold">{h}</th>)}
          </tr>
        </thead>
        <tbody>{rows.map((r) => <Row key={r.id} row={r} onDone={onReload} />)}</tbody>
      </table>
    </div>
  );
}

export function FilterPills({ items, value, onChange, testid }) {
  return (
    <div className="flex rounded-xl bg-[#09090B] border border-zinc-800 overflow-hidden">
      {items.map((s) => (
        <button key={s.v} type="button" onClick={() => onChange(s.v)} data-testid={`${testid}-${s.v}`}
                className={`px-3 h-8 text-[12px] font-bold whitespace-nowrap ${value === s.v ? "bg-zinc-700 text-zinc-100" : "text-zinc-400 hover:bg-zinc-800"}`}>
          {s.l}
        </button>
      ))}
    </div>
  );
}
